import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import { statusLabel, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { TableCard } from "@/components/app/patterns/table-card";
import { DataTable } from "@/components/app/patterns/data-table";
import { RowActions } from "@/components/app/patterns/row-actions";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { optionalOneOf, searchPatch, optionalSearch } from "@/lib/search";
import { useEveryRow } from "@/lib/query/every-row";
import { orpc, orpcClient } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { CAMPAIGN_COLUMNS } from "./-components/campaign-columns";
import { CampaignDialog, type CampaignRow } from "./-components/campaign-dialog";
import { CampaignLeadsPanel } from "./-components/campaign-leads-panel";
import { SearchField } from "@/components/app/patterns/search-field";

const STATUS_FILTERS = ["planned", "active", "completed", "cancelled"] as const;

export const Route = createFileRoute("/_authenticated/_crm/crm/campaigns/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; status?: (typeof STATUS_FILTERS)[number] } => ({
    q: optionalSearch(search.q),
    status: optionalOneOf(STATUS_FILTERS, search.status),
  }),
  component: Campaigns,
});

function Campaigns() {
  const { facilityId } = useFacility();
  const canWrite = useCan("crm:manage");
  const queryClient = useQueryClient();
  const { q, status: picked } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const status = picked ?? "all";
  const refine = (next: { q?: string; status?: (typeof STATUS_FILTERS)[number] }) =>
    patch({ ...next });
  const { text: query, change: setQuery } = useSearchParamText(q, (next) => refine({ q: next }));
  const [editing, setEditing] = useState<CampaignRow | "new" | null>(null);
  const [removing, setRemoving] = useState<CampaignRow | null>(null);

  // Every campaign, however many: the search and status filter run in the browser.
  const campaigns = useEveryRow(
    orpc.crm.campaigns.list.key({ input: { facilityId } }),
    (page, call) => orpcClient.crm.campaigns.list({ facilityId, ...page }, call),
  );
  const remove = useMutation(
    orpc.crm.campaigns.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Campaign deleted");
        await queryClient.invalidateQueries({ queryKey: orpc.crm.campaigns.key() });
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const all = campaigns.data?.rows ?? [];
  const needle = (q ?? "").toLowerCase();
  const rows = all.filter(
    (campaign) =>
      campaign.name.toLowerCase().includes(needle) &&
      (status === "all" || campaign.status === status),
  );
  const filtered = Boolean(needle) || status !== "all";
  const running = rows.filter((campaign) => campaign.status === "active").length;
  const clear = () => {
    setQuery("");
    refine({ q: undefined, status: undefined });
  };
  const create = canWrite ? () => setEditing("new") : undefined;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Campaigns"
        count={campaigns.isPending || campaigns.isError ? undefined : rows.length}
        meta={campaigns.isPending || campaigns.isError ? undefined : `${running} running`}
        actions={
          create ? (
            <Button onClick={create}>
              <Plus className="size-4" />
              Create campaign
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchField placeholder="Search campaigns" value={query} onChange={setQuery} />
        <OptionSelect
          value={status}
          aria-label="Filter by status"
          onChange={(event) =>
            refine({ status: optionalOneOf(STATUS_FILTERS, event.target.value) })
          }
        >
          <SelectOption value="all">All statuses</SelectOption>
          {STATUS_FILTERS.map((option) => (
            <SelectOption key={option} value={option}>
              {statusLabel(option)}
            </SelectOption>
          ))}
        </OptionSelect>
        {filtered ? (
          <Button variant="ghost" onClick={clear}>
            Clear filters
          </Button>
        ) : null}
      </div>

      <TableCard>
        {campaigns.isPending ? (
          <div className="flex flex-col gap-3 p-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={`row-${index}`} className="h-12 w-full" />
            ))}
          </div>
        ) : campaigns.isError ? (
          <ReadFailed
            className="p-4"
            message="Couldn't load campaigns. None are shown because none were read."
            error={campaigns.error}
            retrying={campaigns.isFetching}
            onRetry={() => void campaigns.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={filtered ? "No campaigns match these filters" : "No campaigns yet"}
            description={
              filtered
                ? "Widen the search or clear a filter to see more."
                : "A campaign groups the leads that came from one piece of outreach."
            }
            action={
              filtered ? (
                <Button variant="outline" onClick={clear}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <DataTable
            data={rows}
            columns={CAMPAIGN_COLUMNS}
            noun="campaigns"
            rowId={(campaign) => campaign.id}
            bare
            renderExpanded={(campaign) => (
              <div className="px-4 py-3">
                <CampaignLeadsPanel
                  campaignId={campaign.id}
                  facilityId={campaign.facilityId}
                  canWrite={canWrite}
                />
              </div>
            )}
            renderRowActions={
              canWrite
                ? (campaign) => (
                    <RowActions
                      label={campaign.name}
                      modal={false}
                      items={[
                        {
                          label: "Edit campaign",
                          icon: Pencil,
                          onSelect: () => setEditing(campaign),
                        },
                        {
                          label: "Delete campaign",
                          icon: Trash2,
                          destructive: true,
                          onSelect: () => setRemoving(campaign),
                        },
                      ]}
                    />
                  )
                : undefined
            }
          />
        )}
      </TableCard>

      {editing ? (
        <CampaignDialog
          facilityId={facilityId}
          campaign={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {removing ? (
        <RemoveConfirm
          title={`Delete ${removing.name}?`}
          description="The campaign is removed for good. Its leads stay, without it on their record."
          confirmLabel="Delete campaign"
          onConfirm={() => remove.mutate({ campaignId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}
